import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { DollarSign, TrendingUp, Users, Percent, Loader2, PieChart, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { format, subMonths } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useRole } from "@/contexts/RoleContext";
import { useStageMapping } from "@/hooks/useStageMapping";
import { useSetupStatus } from "@/hooks/useSetupStatus";
import { SetupWarning } from "@/components/ui/setup-warning";
import { SectionInfo } from "@/components/ui/section-info";
import { toast } from "sonner";
import { PageTour } from "@/components/tour/PageTour";
import { salesRevenueSteps } from "@/lib/tour-steps";

export default function SalesRevenue() {
  const { clientId } = useRole();
  const setupStatus = useSetupStatus(clientId);
  const { mapStage, mappings, isLoading: mappingsLoading } = useStageMapping(clientId);
  const [closedDeals, setClosedDeals] = useState<any[]>([]);
  const [allDemos, setAllDemos] = useState<any[]>([]);
  const [funnelEvents, setFunnelEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clientId) { setLoading(false); return; }

    const fetchRevenue = async () => {
      setLoading(true);
      try {
        // Fetch all demos for this client (need all for conversion rate)
        const { data: demosData, error: demosError } = await supabase
          .from('demos')
          .select('id, demo_date, prospect_name, deal_value, outcome, status, closer_id')
          .eq('client_id', clientId)
          .order('demo_date', { ascending: false });

        if (demosError) throw demosError;

        const demos = demosData || [];
        setAllDemos(demos);
        setClosedDeals(demos.filter(d => d.outcome === 'closed'));

        // Fetch funnel events for pipeline revenue
        const { data: eventsData, error: eventsError } = await supabase
          .from('funnel_events')
          .select('stage, deal_value_cents')
          .eq('client_id', clientId);

        if (eventsError) throw eventsError;
        setFunnelEvents(eventsData || []);
      } catch (error: unknown) {
        console.error('Error fetching revenue data:', error);
        toast.error('Failed to load revenue data');
      } finally {
        setLoading(false);
      }
    };

    fetchRevenue();
  }, [clientId]);

  // Pipeline revenue from funnel_events where stage maps to closed_won
  const hasMappings = mappings.length > 0;
  const pipelineRevenueCents = hasMappings
    ? funnelEvents
        .filter(e => mapStage(e.stage) === 'closed_won')
        .reduce((sum, e) => sum + (e.deal_value_cents || 0), 0)
    : 0;
  const pipelineRevenue = pipelineRevenueCents / 100;

  const totalRevenue = closedDeals.reduce((sum, d) => sum + (d.deal_value || 0), 0);

  const now = new Date();
  const demosClosedThisMonth = closedDeals.filter(d => {
    const demoDate = new Date(d.demo_date);
    return demoDate.getMonth() === now.getMonth() && demoDate.getFullYear() === now.getFullYear();
  }).length;

  const avgDealSize = closedDeals.length > 0 ? totalRevenue / closedDeals.length : 0;

  // Conversion rate: closed deals / total completed demos
  const completedDemos = allDemos.filter(d => d.status === 'completed');
  const conversionRate = completedDemos.length > 0
    ? Math.round((closedDeals.length / completedDemos.length) * 100)
    : 0;

  const stats = [
    // Pipeline Revenue card — only shown when stage mappings exist
    ...(hasMappings ? [{
      label: "Pipeline Revenue",
      tooltip: "Revenue from deals in stages mapped to 'Closed Won' in your GHL pipeline",
      value: pipelineRevenue >= 1000 ? `$${(pipelineRevenue / 1000).toFixed(1)}K` : `$${pipelineRevenue.toLocaleString()}`,
      icon: PieChart,
      color: "text-purple-400",
    }] : []),
    {
      label: "Total Revenue Generated",
      tooltip: null as string | null,
      value: totalRevenue >= 1000 ? `$${(totalRevenue / 1000).toFixed(0)}K` : `$${totalRevenue.toLocaleString()}`,
      icon: DollarSign,
      color: "text-green-400",
    },
    {
      label: "Demos Closed This Month",
      tooltip: null as string | null,
      value: demosClosedThisMonth,
      icon: Users,
      color: "text-secondary",
    },
    {
      label: "Average Deal Size",
      tooltip: "Mean revenue per closed deal",
      value: avgDealSize >= 1000 ? `$${(avgDealSize / 1000).toFixed(1)}K` : `$${avgDealSize.toLocaleString()}`,
      icon: TrendingUp,
      color: "text-blue-400",
    },
    {
      label: "Conversion Rate",
      tooltip: "Percentage of completed demos that resulted in closed deals",
      value: `${conversionRate}%`,
      icon: Percent,
      color: "text-amber-400",
    },
  ];

  // Compute monthly revenue for last 6 months
  const monthlyRevenue = Array.from({ length: 6 }, (_, i) => {
    const monthDate = subMonths(now, 5 - i);
    const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);

    const monthDeals = closedDeals.filter(d => {
      const demoDate = new Date(d.demo_date);
      return demoDate >= monthStart && demoDate <= monthEnd;
    });

    return {
      month: format(monthDate, 'MMM'),
      revenue: monthDeals.reduce((sum, d) => sum + (d.deal_value || 0), 0),
    };
  });

  const maxRevenue = Math.max(...monthlyRevenue.map(m => m.revenue), 1);

  if (loading || mappingsLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!loading && allDemos.length === 0) {
    return (
      <motion.div className="space-y-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Sales & Revenue</h1>
          <p className="text-muted-foreground">Revenue generated by Venture Hills for your business</p>
        </div>
        <VentureCard>
          <VentureCardContent className="text-center py-12">
            <DollarSign className="w-12 h-12 mx-auto text-muted-foreground mb-4 opacity-50" />
            <p className="text-muted-foreground">No demo data available yet</p>
            <p className="text-sm text-muted-foreground mt-2">Revenue data will appear once demos are recorded</p>
          </VentureCardContent>
        </VentureCard>
      </motion.div>
    );
  }

  return (
    <>
    <PageTour steps={salesRevenueSteps} />
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold">Sales & Revenue</h1>
        <p className="text-muted-foreground">Revenue generated by Venture Hills for your business</p>
        <SectionInfo description="Closed deal revenue tracked through your GHL pipeline." />
      </div>

      {!setupStatus.loading && (
        <div className="space-y-2">
          {!setupStatus.ghlConnected && (
            <SetupWarning message="GHL is not connected — revenue tracking is unavailable." />
          )}
          {setupStatus.ghlConnected && !setupStatus.stageMappingsConfigured && (
            <SetupWarning message="Pipeline stages are being configured — revenue data will appear once mapping is complete." />
          )}
        </div>
      )}

      {/* Stats */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${hasMappings ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-4`}>
        {stats.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <VentureCard>
              <VentureCardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className={`p-2 rounded-lg bg-surface ${stat.color}`}>
                    <stat.icon className="w-5 h-5" />
                  </div>
                </div>
                <p className="text-2xl font-bold">{stat.value}</p>
                <p className="text-sm text-muted-foreground">
                  {stat.label}
                  {stat.tooltip && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-3 h-3 text-muted-foreground inline ml-1 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{stat.tooltip}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </p>
              </VentureCardContent>
            </VentureCard>
          </motion.div>
        ))}
      </div>

      {/* Revenue Chart */}
      <VentureCard>
        <VentureCardHeader>
          <VentureCardTitle>Monthly Revenue Generated</VentureCardTitle>
        </VentureCardHeader>
        <VentureCardContent>
          <div className="h-64 flex items-end justify-between gap-4 px-4">
            {monthlyRevenue.map((month, index) => {
              const height = (month.revenue / maxRevenue) * 100;
              return (
                <div key={month.month} className="flex-1 flex flex-col items-center gap-2">
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: `${height}%` }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    className="w-full bg-gradient-to-t from-secondary/50 to-secondary rounded-t-lg min-h-[20px]"
                  />
                  <span className="text-xs text-muted-foreground">{month.month}</span>
                  <span className="text-xs font-medium">${(month.revenue / 1000).toFixed(0)}K</span>
                </div>
              );
            })}
          </div>
        </VentureCardContent>
      </VentureCard>

      {/* Recent Deals */}
      <VentureCard>
        <VentureCardHeader>
          <VentureCardTitle>Recent Closed Deals</VentureCardTitle>
        </VentureCardHeader>
        <VentureCardContent>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Date</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Prospect</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Deal Amount</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {closedDeals.slice(0, 10).map((deal) => (
                  <tr key={deal.id} className="border-b border-border hover:bg-surface transition-colors">
                    <td className="py-3 px-4 text-muted-foreground">
                      {format(new Date(deal.demo_date), 'MMM d, yyyy')}
                    </td>
                    <td className="py-3 px-4 font-medium">{deal.prospect_name || 'Unknown'}</td>
                    <td className="py-3 px-4 text-green-400 font-medium">
                      ${(deal.deal_value || 0).toLocaleString()}
                    </td>
                    <td className="py-3 px-4">
                      <BadgeVenture variant="success">Closed Won</BadgeVenture>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </VentureCardContent>
      </VentureCard>
    </motion.div>
    </>
  );
}
